import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);
const INACTIVITY_MS = 60 * 60 * 1000; // 1 hour

export function AuthProvider({ children }) {
  // null => checking, false => not authed, object => user
  const [user, setUser] = useState(null);
  const timerRef = useRef(null);

  const checkSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("logout request failed", e); }
    setUser(false);
  }, []);

  // Inactivity auto-logout
  useEffect(() => {
    if (!user || user === false) return;
    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        toast.info("Signed out after 1 hour of inactivity");
        await logout();
      }, INACTIVITY_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user, logout]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const value = useMemo(
    () => ({ user, setUser, login, logout, checkSession }),
    [user, logout, checkSession]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
